import { Box, Grid, Typography, Button } from "@mui/material";
import { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { LuCalculator, LuChartColumn, LuCoins, LuTable } from "react-icons/lu";
import SummarizeIcon from "@mui/icons-material/Summarize";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import { Banner, WorkspaceHeader } from "../../components/m3";
import CircularityReportButton from "../../circularity-components/CircularityReportButton";
import CircularityKpiTiles from "../../circularity-components/CircularityKpiTiles";
import { useNavigate } from "react-router-dom";
import { getOrgTokens } from "../../theme/style/organizationPalette";
import CircularChart from "../../circularity-components/CircularChart";
import CircularPCIChart from "../../circularity-components/CircularPCIChart";
import MaterialDetailCard from "../../circularity-components/MaterialDetailCard";
import NomarlizedGwpVsPercentageMciChart from "../../circularity-components/NomarlizedGwpVsPercentageMciChart";
import NomarlizedGwpVsPercentagePciChart from "../../circularity-components/NomarlizedGwpVsPercentagePciChart";
import { computePciData } from "../../circularity-components/computePciData";
import ProjectMCICalculationTable from "../../circularity-components/ProjectMCICalculationTable";
import SaveButton from "../../components/button/SaveButton";
import { MainContainer } from "../../components/container/MainContainer";
import { PageTitleContainer } from "../../components/container/PageTitleContainer";
import { useContextProvider } from "../../components/page-context/ContextProvider";
import DesignAssessmentPanel from "../Products/Circularity/DesignAssessmentPanel";
import ScenarioPanel from "../Products/Circularity/ScenarioPanel";
import BenchmarkPanel from "../Products/Circularity/BenchmarkPanel";
import CircularityReportPanel from "../Products/Circularity/CircularityReportPanel";
import ScopeBoundaryPanel from "../Products/Circularity/ScopeBoundaryPanel";
import CircularityAdminTabControls, { useCircularityAdminTabs } from "../../circularity-components/CircularityAdminTabControls";
import { useInvalidateProjectCache } from "../../utilities/useProjectQueries";
import { Circularity } from "../../db_access/circularity";
import { EnvFactor } from "../../db_access/env_factor";
import { Project } from "../../db_access/project";
import { calculate_combined_data, recalculate_row_mci } from "../../functions/CircularityFunctions";
import routing from "../../routing";
import "../../style/components-style/Table.css";
import "../../style/pages-style/Circularity.css";
import CircularityFlowChart from "../Products/Circularity/CircularityFlowChart";
import { getDefaultDisassemblyFactors } from "../Products/Circularity/pciOptions";
import MaterialCompositionTable from "../Products/Circularity/MaterialCompositionTable";
import UtilityTable from "../Products/Circularity/UtilityTable";
import config from "../../config";

const ProjectCircularity = () => {
  const { t } = useTranslation();
  const { selectedProjects, refresh, setRefresh, setIsSaveVisible, setIsLoading, isSaveClicked, setIsSaveClicked, isDark, enqueueAlert, setLeftSidebarCollapse } = useContextProvider();
  const navigate = useNavigate();
  const scope_boundary = { label: "Boundary Settings", value: "Boundary Settings" };
  const overview = { label: "Overview", value: "Overview" };
  const utility = { label: "Utility", value: "Utility" };
  const mci_calculation = { label: "MCI Calculation", value: "MCI Calculation" };
  const analytics = { label: "Analytics", value: "Analytics" };

  const [scopeDesign, setScopeDesign] = useState(false);
  const [scopeScenario, setScopeScenario] = useState(false);

  const design_assessment = { label: "Design Assessment", value: "Design Assessment" };
  const scenario_what_if = { label: "Scenario/What-if", value: "Scenario/What-if" };
  const benchmarking = { label: "AI Recommendation", value: "AI Recommendation" };
  const pcds_report = { label: "Circularity Report", value: "Circularity Report", tabIcon: SummarizeIcon };
  const {
    isAdmin,
    adminTabsPrefs,
    updatePref,
    setAllPrefs,
    showUtilityTab,
    showMciCalcTab,
    showDesignAssessmentTab,
    showAiRecommendTab,
    activeAdminTabsCount,
  } = useCircularityAdminTabs();

  const isDesignAssessmentVisible = scopeDesign || showDesignAssessmentTab;

  const [materials, setMaterials] = useState([]);
  const [circularities, setCircularities] = useState([]);
  const [selectedEnvFactorID, setSelectedEnvFactorID] = useState(null);
  const [selectedProject, setSelectedProject] = useState();
  const [activeTab, setActiveTab] = useState(overview.value);

  // Auto-switch to overview when a hidden tab is active
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (activeTab === design_assessment.value && !isDesignAssessmentVisible) setActiveTab(overview.value);
    if (activeTab === scenario_what_if.value && !scopeScenario) setActiveTab(overview.value);
    if (activeTab === utility.value && !showUtilityTab) setActiveTab(overview.value);
    if (activeTab === mci_calculation.value && !showMciCalcTab) setActiveTab(overview.value);
    if (activeTab === benchmarking.value && !showAiRecommendTab) setActiveTab(overview.value);
  }, [isDesignAssessmentVisible, scopeScenario, showUtilityTab, showMciCalcTab, showAiRecommendTab, activeTab]);

  useEffect(() => {
    if (activeTab === design_assessment.value) {
      setLeftSidebarCollapse(true);
    }
  }, [activeTab, setLeftSidebarCollapse]);

  const [product_mci, setProduct_mci] = useState(0);
  const [percentage_product_mci, setPercentage_product_mci] = useState(0);
  const [mci_sum, setMci_sum] = useState(0);
  const [mass_sum, setMass_sum] = useState(0);
  const [cost_sum, setCost_sum] = useState(0);
  const [gwp_total_sum, setGwp_total_sum] = useState(0);
  const [combined_circularities, setCombined_circularities] = useState([]);
  const [service_life, setService_life] = useState(0);
  const tabs = [overview, utility, mci_calculation, analytics];

  // AI State Variables
  const [aiLoading, setAiLoading] = useState(false);
  const aiTriggeredForId = useRef(null);
  const [showInfoBar, setShowInfoBar] = useState(false);

  // Ref for the silent MCI auto-sync debounce timer
  const mciSyncTimeoutRef = useRef(null);
  const { invalidateDashboard } = useInvalidateProjectCache();
  const [isNewProduct, setIsNewProduct] = useState(false);

  // Indicator scope toggles (Scope & Boundary) — shared with the Utility tab's sub-tabs
  const [scopeEnergy, setScopeEnergy] = useState(false);
  const [scopeWater, setScopeWater] = useState(false);
  const [scopeEconomic, setScopeEconomic] = useState(false);

  // ScopeBoundaryPanel lifted configurations
  const [start, setStart] = useState("2026-01-01");
  const [end, setEnd] = useState("2026-12-31");
  const [cutoff, setCutoff] = useState("Fiscal year-end");
  const [flow, setFlow] = useState("gate");
  const [materiality, setMateriality] = useState(95);

  const boundaryState = useMemo(() => ({
    start, end, cutoff, flow, materiality
  }), [start, end, cutoff, flow, materiality]);

  // ─── Persist Scope & Boundary toggles per project ──────────────────────────
  // Survives a page refresh or navigating away and back.
  const scopeTogglesKey = selectedProject?.id ? `arivu_project_scope_toggles_${selectedProject.id}` : null;
  const scopeTogglesLoaded = useRef(false);

  useEffect(() => {
    scopeTogglesLoaded.current = false;
    if (!scopeTogglesKey) return;
    try {
      const saved = localStorage.getItem(scopeTogglesKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        setScopeEnergy(!!parsed.scopeEnergy);
        setScopeWater(!!parsed.scopeWater);
        setScopeEconomic(!!parsed.scopeEconomic);
        setScopeDesign(!!parsed.scopeDesign);
        setScopeScenario(!!parsed.scopeScenario);
        if (parsed.start) setStart(parsed.start);
        if (parsed.end) setEnd(parsed.end);
        if (parsed.cutoff) setCutoff(parsed.cutoff);
        if (parsed.flow) setFlow(parsed.flow);
        if (typeof parsed.materiality === "number") setMateriality(parsed.materiality);
      } else {
        setScopeEnergy(false);
        setScopeWater(false);
        setScopeEconomic(false);
        setScopeDesign(false);
        setScopeScenario(false);
      }
    } catch (e) {
      console.error("Failed to load Scope & Boundary toggle preferences", e);
    } finally {
      scopeTogglesLoaded.current = true;
    }
  }, [scopeTogglesKey]);

  useEffect(() => {
    // Skip the write that would otherwise fire immediately after the load effect above
    // sets these same values back onto state (harmless, but avoids a redundant write).
    if (!scopeTogglesKey || !scopeTogglesLoaded.current) return;
    localStorage.setItem(
      scopeTogglesKey,
      JSON.stringify({
        scopeEnergy,
        scopeWater,
        scopeEconomic,
        scopeDesign,
        scopeScenario,
        start,
        end,
        cutoff,
        flow,
        materiality,
      })
    );
  }, [
    scopeTogglesKey,
    scopeEnergy,
    scopeWater,
    scopeEconomic,
    scopeDesign,
    scopeScenario,
    start,
    end,
    cutoff,
    flow,
    materiality,
  ]);

  const combined_data = useMemo(() => {
    return { mass_sum, cost_sum, gwp_total_sum, mci_sum, product_mci, percentage_product_mci, combined_circularities, service_life };
  }, [mass_sum, cost_sum, gwp_total_sum, mci_sum, product_mci, percentage_product_mci, combined_circularities, service_life]);

  const pciData = useMemo(() => {
    return computePciData(combined_data, "Project", selectedProject?.id);
  }, [combined_data, selectedProject?.id]);

  const hasPci = useMemo(() => {
    return (pciData?.percentageOverallPci || 0) > 0;
  }, [pciData]);

  const selected_material = useMemo(() => {
    return combined_data.combined_circularities.find((m) => m.env_id === selectedEnvFactorID);
  }, [combined_data.combined_circularities, selectedEnvFactorID]);

  const handleSmartFill = useCallback(async (currentCircularities, currentMaterials) => {
    if (!currentCircularities?.length || !currentMaterials?.length || aiLoading) return;

    setAiLoading(true);
    aiTriggeredForId.current = selectedProject?.id;

    try {
      const sortedMaterials = [...currentMaterials].sort((a, b) => String(a.id).localeCompare(String(b.id)));

      // Extract user context for AI token tracking
      let userContext = {};
      try {
        const storedUser = localStorage.getItem("user");
        if (storedUser) {
          const u = JSON.parse(storedUser);
          userContext = { user_id: u.id || u.user_id, company_id: u.company_id };
        }
      } catch (e) {
        // ignore parse error
      }

      const response = await Circularity.ai_suggest({
        materials: sortedMaterials,
        product_context: {
          name: selectedProject?.name || selectedProject?.project_name,
          category: selectedProject?.category || selectedProject?.type,
          service_life: service_life,
        },
        ...userContext,
      });

      if (!response.ok) throw new Error("AI Suggestion request failed");
      const allSuggestions = await response.json();

      const updatedCircularities = currentCircularities.map((c) => {
        const materialIndex = sortedMaterials.findIndex(m => String(m.id) === String(c.env_id) || String(m.id) === String(c.id));
        const sug = materialIndex !== -1 ? allSuggestions[String(materialIndex)] : null;

        if (sug) {
          // Resolve im_recycled_content: prefer AI suggestion, fall back to existing value
          const aiRecycledContent = sug.im_recycled_content !== null && sug.im_recycled_content !== undefined
            ? Number(sug.im_recycled_content)
            : (c.im_recycled_content !== undefined ? Number(c.im_recycled_content) : 0);

          // im_virgin_content is always derived: 100 - recycled (system rule — never from AI directly)
          const derivedVirginContent = Math.max(0, Math.min(100, 100 - aiRecycledContent));

          // om_recycling: prefer AI suggestion, fall back to existing value
          const aiOmRecycling = sug.om_recycling !== null && sug.om_recycling !== undefined
            ? Number(sug.om_recycling)
            : (c.om_recycling !== undefined ? Number(c.om_recycling) : 80);

          // Industry average service life from AI
          const aiIndustryLife = sug.uf_industry_avg_life !== undefined
            ? Number(sug.uf_industry_avg_life)
            : (sug.rsl !== undefined ? Number(sug.rsl) : (Number(c.uf_industry_avg_life) || 20));

          // Product life: prefer project service_life if set > 0, otherwise AI industry avg, otherwise existing > 0, fallback 20
          const resolvedProductLife = Number(service_life) > 0
            ? Number(service_life)
            : (aiIndustryLife || (Number(c.uf_product_life) > 0 ? Number(c.uf_product_life) : 20));

          const updated = {
            ...c,
            // 1. Utility tab: Reference Service Life & Industry Average
            uf_product_life: resolvedProductLife,
            uf_industry_avg_life: aiIndustryLife,
            // 2. End of Life tab: Recycling Efficiency After Use & Feedstock Efficiency
            eol_recycling_efficiency: sug.eol_recycling_efficiency !== undefined ? Number(sug.eol_recycling_efficiency) : (sug.efficiency !== undefined ? Number(sug.efficiency) : (c.eol_recycling_efficiency || 90)),
            eol_feedstock_efficiency: sug.eol_feedstock_efficiency !== undefined ? Number(sug.eol_feedstock_efficiency) : (c.eol_feedstock_efficiency || 90),
            // 3. Output tab: Sent to Recycling (%)
            om_recycling: aiOmRecycling,
            // 4. Input tab: Recycled Content (%) and derived Virgin Content (%)
            im_recycled_content: aiRecycledContent,
            im_virgin_content: derivedVirginContent,
          };
          return recalculate_row_mci(updated);
        }
        return c;
      });

      setCircularities(updatedCircularities);
      setShowInfoBar(true);
      setAiLoading(false);
      setIsSaveVisible(true);

    } catch (error) {
      console.error("[AI] Error:", error);
      setAiLoading(false);
    }
  }, [selectedProject, service_life, aiLoading, setIsSaveVisible]);

  useEffect(() => {
    if (!selectedProject?.id) {
      return;
    }
    Project.get_by_id(selectedProject.id)
      .then((res) => res.json())
      .then((data) => {
        setService_life(data?.[0]?.service_life || 0);
        setIsSaveVisible(false);
        setIsLoading(false);
        setIsSaveClicked(false);
      });
  }, [selectedProject?.id, setIsLoading, setIsSaveClicked, setIsSaveVisible]);

  useEffect(() => {
    if (selectedProjects.length === 1) {
      setSelectedProject(selectedProjects[0]);
    } else {
      setSelectedProject(undefined);
      navigate(routing.sector.projects.dashboard);
    }
  }, [selectedProjects]);

  useEffect(() => {
    if (!selectedProject?.id) {
      setMaterials([]);
      return;
    }
    EnvFactor.get_materials_by_project_id(selectedProject.id)
      .then((response) => response.json())
      .then((data) => {
        setMaterials(data);
      });
  }, [selectedProject?.id, refresh]);

  useEffect(() => {
    if (materials.length === 0) {
      setCircularities([]);
      return;
    }
    Circularity.get_by_env_factor_ids(materials.map((item) => item.id))
      .then((res) => res.json())
      .then((response) => {
        const data = response.data || [];
        setCircularities(data);
        setIsNewProduct(!!response.is_new);
      });
  }, [materials]);

  useEffect(() => {
    const alreadyTriggered = aiTriggeredForId.current === selectedProject?.id;

    if (isNewProduct && !alreadyTriggered && !aiLoading && circularities.length > 0) {
      const timer = setTimeout(() => {
        handleSmartFill(circularities, materials);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [circularities, materials, isNewProduct, selectedProject?.id, aiLoading, handleSmartFill]);

  useEffect(() => {
    if (materials.length === 0) return;

    // IMPORTANT: env_factor IDs are UUIDs (strings). Number() on a UUID = NaN.
    // NaN === NaN is always false. Must use String() comparison — same as ProductCircularity.
    const t0 = materials.map((m) => {
      const c = circularities.find((circ) => String(circ.env_id) === String(m.id) || String(circ.id) === String(m.id));
      return {
        ...m,
        ...(c || {}),
        description: m.description || c?.description || "Unknown Material",
        ct_factor: c?.ct_factor != null ? c.ct_factor : (m.ct_factor ?? getDefaultDisassemblyFactors(c).ct),
        ca_factor: c?.ca_factor != null ? c.ca_factor : (m.ca_factor ?? getDefaultDisassemblyFactors(c).ca),
        indep_factor: c?.indep_factor != null ? c.indep_factor : (m.indep_factor ?? getDefaultDisassemblyFactors(c).indep),
        gpe_factor: c?.gpe_factor != null ? c.gpe_factor : (m.gpe_factor ?? getDefaultDisassemblyFactors(c).gpe),
      };
    });

    if (circularities.length > 0) {
      const { mass_sum, gwp_total_sum, cost_sum, mci_sum, product_mci, percentage_product_mci, combined_circularities } = calculate_combined_data(t0);
      setMass_sum(mass_sum);
      setCost_sum(cost_sum);
      setGwp_total_sum(gwp_total_sum);
      setMci_sum(mci_sum);
      setProduct_mci(product_mci);
      setPercentage_product_mci(percentage_product_mci);
      setCombined_circularities(combined_circularities);
    } else {
      // Show materials in the table even if circularity records haven't been created yet
      setCombined_circularities(t0);
    }
  }, [circularities, materials]);

  // ─── Silent MCI Auto-Sync ────────────────────────────────────────────────
  // When the Circularity page computes a non-zero MCI, immediately persist it
  // to project.mci in the DB (debounced 1.5 s) so the Dashboard and Metric
  // cards reflect the live value without the user having to click Save.
  useEffect(() => {
    if (!selectedProject?.id || !(percentage_product_mci > 0)) return;

    if (mciSyncTimeoutRef.current) clearTimeout(mciSyncTimeoutRef.current);

    mciSyncTimeoutRef.current = setTimeout(async () => {
      try {
        const res = await Project.get_by_id(selectedProject.id);
        const data = await res.json();
        if (data && data[0]) {
          await Project.update({
            ...data[0],
            mci: percentage_product_mci,
            product_mci: percentage_product_mci,
          });
          console.log('[ProjectCircularity] 🔄 Auto-synced MCI to DB:', (percentage_product_mci * 100).toFixed(2) + '%');
          // Invalidate the dashboard cache so ProjectDashboard picks up the new value
          invalidateDashboard();
        }
      } catch (err) {
        console.error('[ProjectCircularity] ❌ Auto-sync MCI failed:', err);
      }
    }, 1500);

    return () => {
      if (mciSyncTimeoutRef.current) clearTimeout(mciSyncTimeoutRef.current);
    };
  }, [percentage_product_mci, selectedProject?.id]);
  // ─────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (isSaveClicked && selectedProject) {
      console.log('[ProjectCircularity] 🔄 Fetching latest project data for safe sync...');
      setIsLoading(true);

      const fullPayload = circularities.map((c) => ({
        ...c,
        id: !isNaN(Number(c.id)) ? Number(c.id) : c.id,
        env_id: c.env_id || c.id,
        ct_factor: c.ct_factor != null ? Number(c.ct_factor) : getDefaultDisassemblyFactors(c).ct,
        ca_factor: c.ca_factor != null ? Number(c.ca_factor) : getDefaultDisassemblyFactors(c).ca,
        indep_factor: c.indep_factor != null ? Number(c.indep_factor) : getDefaultDisassemblyFactors(c).indep,
        gpe_factor: c.gpe_factor != null ? Number(c.gpe_factor) : getDefaultDisassemblyFactors(c).gpe,
      }));

      Promise.all([
        Circularity.update_utilities(fullPayload),
        Circularity.update_end_of_lifes_materials(fullPayload),
        Circularity.update_input_materials(fullPayload),
        Circularity.update_output_materials(fullPayload),
        Project.get_by_id(selectedProject.id)
          .then(res => res.json())
          .then(data => {
            if (data && data[0]) {
              const fullProject = data[0];
              console.log('[ProjectCircularity] 🔄 Syncing MCI to Dashboard (Full Object):', percentage_product_mci);

              // Safer update: Use full object from DB
              const updatePayload = {
                ...fullProject,
                mci: percentage_product_mci,
                product_mci: percentage_product_mci
              };

              return Project.update(updatePayload);
            }
          })
      ])
        .then(() => {
          console.log('[ProjectCircularity] ✅ All data and MCI successfully saved & synced!');
          setIsLoading(false);
          setIsSaveClicked(false);
          setIsSaveVisible(false);
          setRefresh(r => !r);
          setIsNewProduct(false);
          if (enqueueAlert) enqueueAlert({ message: t("Data saved successfully"), severity: "success" });
        })
        .catch(err => {
          console.error('[ProjectCircularity] ❌ Save failed:', err);
          setIsLoading(false);
          setIsSaveClicked(false);
          if (enqueueAlert) enqueueAlert({ message: t("Save failed. Please check your network."), severity: "error" });
        });
    }
  }, [isSaveClicked, selectedProject, percentage_product_mci, circularities, setIsLoading, setIsSaveClicked, setIsSaveVisible, setRefresh, enqueueAlert, t]);


  const openReportTab = useCallback(() => setActiveTab(pcds_report.value), [pcds_report.value]);
  const htmlReportInputs = useMemo(
    () => ({ entity: selectedProject, topic: "Project", combined_data, pciData, boundaryState, serviceLife: service_life }),
    [selectedProject, combined_data, pciData, boundaryState, service_life]
  );

  const tok = getOrgTokens(isDark);

  return (
    <MainContainer>
      <Box sx={{ mb: tok.spacing.pageGap }}>
        <CircularityKpiTiles
          massKg={mass_sum}
          gwpKg={gwp_total_sum}
          mciPercent={percentage_product_mci * 100}
          pciPercent={pciData?.percentageOverallPci || 0}
          rows={combined_data.combined_circularities}
          topic="Project"
        />
      </Box>

      <Box sx={{ mb: tok.spacing.pageGap }}>
        <WorkspaceHeader
          tabs={[
            overview,
            ...(showUtilityTab ? [utility] : []),
            ...(showMciCalcTab ? [mci_calculation] : []),
            ...(isDesignAssessmentVisible ? [design_assessment] : []),
            analytics,
            ...(scopeScenario ? [scenario_what_if] : []),
            ...(showAiRecommendTab ? [benchmarking] : []),
          ].map((tab) => ({ value: tab.value, label: t(tab.label) }))}
          value={activeTab}
          onChange={setActiveTab}
          ariaLabel={t("Circularity sections")}
          actions={
            <>
              {isAdmin && (
                <CircularityAdminTabControls
                  adminTabsPrefs={adminTabsPrefs}
                  updatePref={updatePref}
                  setAllPrefs={setAllPrefs}
                  activeAdminTabsCount={activeAdminTabsCount}
                  isDark={isDark}
                />
              )}
              <CircularityReportButton
                active={activeTab === pcds_report.value}
                onOpenReport={openReportTab}
                reportInputs={htmlReportInputs}
              />
            </>
          }
        />
      </Box>

      {activeTab === design_assessment.value && (
        <Box sx={{ mb: 3 }}>
          <DesignAssessmentPanel
            combined_data={combined_data}
            type="Project"
            id={selectedProject?.id}
            setCircularities={setCircularities}
            circularities={circularities}
          />
        </Box>
      )}

      {activeTab === scenario_what_if.value && (
        <Box sx={{ mb: 3 }}>
          <ScenarioPanel />
        </Box>
      )}

      {showAiRecommendTab && activeTab === benchmarking.value && (
        <Box sx={{ mb: 3 }}>
          <BenchmarkPanel combined_data={combined_data} />
        </Box>
      )}

      {activeTab === pcds_report.value && (
        <Box sx={{ mb: 3 }}>
          <CircularityReportPanel
            scopeEnergy={scopeEnergy}
            scopeWater={scopeWater}
            scopeEconomic={scopeEconomic}
            reportTitle="Project Circularity"
            productName={selectedProject?.name}
            productId={selectedProject?.id}
            productSubCategory={selectedProject?.sub_category || "Project / Building"}
            serviceLife={service_life}
            combined_data={combined_data}
            boundaryState={boundaryState}
            topic="Project"
          />
        </Box>
      )}

      <Box sx={{ display: activeTab === overview.value ? "block" : "none" }}>
        <MaterialCompositionTable
          combined_data={combined_data}
          circularities={circularities}
          setCircularities={setCircularities}
          materials={materials}
          aiLoading={aiLoading}
          handleSmartFill={handleSmartFill}
          type="Project"
          id={selectedProject?.id}
          activeTab={activeTab}
          tabValue={overview.value}
        />
      </Box>

      <Box sx={{ display: activeTab === utility.value ? "block" : "none" }}>
        <UtilityTable
          combined_data={combined_data}
          activeTab={activeTab}
          tabValue={utility.value}
          setCircularities={setCircularities}
          materials={materials}
          aiLoading={aiLoading}
          handleSmartFill={handleSmartFill}
          scopeEnergy={scopeEnergy}
          scopeWater={scopeWater}
          scopeEconomic={scopeEconomic}
          topic="Project"
        />
      </Box>

      <Box sx={{ display: activeTab === mci_calculation.value ? "block" : "none" }}>
        <ProjectMCICalculationTable combined_data={combined_data} topic="Project" />
      </Box>

      {activeTab === analytics.value && (
        <Box sx={{ display: "flex", flexWrap: "wrap", flexDirection: "column", gap: 3 }}>
          <Grid container spacing={3}>
            {hasPci ? (
              <>
                {/* Adjacent Row 1: Material Circularity Indicator & Project Circularity Indicator */}
                <Grid size={{ xs: 12, md: 6 }} sx={{ height: 520 }}>
                  <CircularChart setSelectedEnvFactorID={setSelectedEnvFactorID} title="Material Circularity Indicator" topic="Project" combined_data={combined_data} />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }} sx={{ height: 520 }}>
                  <CircularPCIChart
                    setSelectedEnvFactorID={setSelectedEnvFactorID}
                    title="Project Circularity Indicator"
                    topic="Project"
                    combined_data={combined_data}
                    productId={selectedProject?.id}
                  />
                </Grid>

                {/* Adjacent Row 2: GWP vs MCI & GWP vs PCI */}
                <Grid size={{ xs: 12, md: 6 }} sx={{ height: 520 }}>
                  <NomarlizedGwpVsPercentageMciChart topic="Project" combined_data={combined_data} />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }} sx={{ height: 520 }}>
                  <NomarlizedGwpVsPercentagePciChart
                    topic="Project"
                    combined_data={combined_data}
                    productId={selectedProject?.id}
                  />
                </Grid>
              </>
            ) : (
              <>
                {/* Default Layout: Material Circularity Indicator & GWP vs MCI side-by-side */}
                <Grid size={{ xs: 12, md: 6 }} sx={{ height: 520 }}>
                  <CircularChart setSelectedEnvFactorID={setSelectedEnvFactorID} title="Material Circularity Indicator" topic="Project" combined_data={combined_data} />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }} sx={{ height: 520 }}>
                  <NomarlizedGwpVsPercentageMciChart topic="Project" combined_data={combined_data} />
                </Grid>
              </>
            )}

            {/* Input -> end-of-life material flows, one row per material */}
            <Grid size={{ xs: 12 }}>
              <CircularityFlowChart circularities={combined_data.combined_circularities} />
            </Grid>
          </Grid>
          {selectedEnvFactorID && (
            <Box sx={{ mt: 2 }}>
              <MaterialDetailCard material={selected_material} />
            </Box>
          )}
        </Box>
      )}

      {showInfoBar && (
        <Box sx={{ mt: tok.spacing.sectionGap }}>
          <Banner tone="success" icon={<AutoAwesomeOutlinedIcon />} onDismiss={() => setShowInfoBar(false)}>
            <strong>{t("Intelligent Estimation Applied")}</strong>{" "}
            {t("Recycling efficiency and product life parameters have been estimated for the identified materials ({{count}} items). Please review and Save.", { count: materials.length })}
          </Banner>
        </Box>
      )}

      <Box sx={{ mt: 4 }}>
        <SaveButton />
      </Box>
    </MainContainer>
  );
};

export default ProjectCircularity;
